/**
 * Gate component. Returning visit: passcode unlocks OPFS envelope.
 */
import { store } from '../store.ts';
import { decryptVault } from '../chassis/crypto.ts';
import { loadEnvelope } from '../chassis/storage.ts';
import type { Vault } from '../types.ts';

const TPL = /* html */ `
<style>
:host {
  display: grid;
  place-items: center;
  inset: 0;
  position: fixed;
  background:
    radial-gradient(ellipse 70% 50% at 50% -10%, oklch(96% 0.020 80), transparent 65%),
    radial-gradient(ellipse 120% 80% at 50% 110%, oklch(82% 0.045 70 / 0.5), transparent 60%),
    var(--parchment);
  overflow: hidden;
  padding: 28px 20px;
}
:host::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    repeating-linear-gradient(
      to bottom,
      transparent 0 27px,
      oklch(60% 0.040 50 / 0.075) 27px 28px
    );
  opacity: 0.55;
}
.cover {
  position: relative;
  z-index: 2;
  max-width: 440px;
  width: 100%;
  display: grid;
  gap: 12px;
  padding: 36px 28px;
  text-align: center;
}
.top {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px;
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: var(--ink-dim);
  padding-bottom: 16px;
  border-bottom: 1px solid var(--ink);
}
.top .center { display: flex; align-items: center; gap: 8px; justify-content: center; }
.top .dot { width: 4px; height: 4px; background: var(--indigo); border-radius: 50%; display: inline-block; }
.mark {
  font-family: var(--hindi);
  font-size: clamp(98px, 24vw, 152px);
  color: var(--indigo);
  line-height: 0.88;
  letter-spacing: -0.02em;
}
.en {
  font-family: var(--display);
  font-style: italic;
  font-weight: 600;
  font-size: clamp(26px, 7vw, 36px);
  color: var(--ink);
  margin-top: -2px;
}
.en::before, .en::after {
  content: '·';
  color: var(--indigo);
  margin: 0 12px;
  opacity: 0.7;
}
.quote {
  font-family: var(--display);
  font-size: 17px;
  font-style: italic;
  color: var(--ink);
  margin: 18px 0 4px;
  text-wrap: balance;
}
.attr {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 18px;
}
form { display: grid; gap: 10px; margin-top: 8px; }
input {
  font-family: var(--mono);
  font-size: 18px;
  text-align: center;
  padding: 18px 16px;
  background: oklch(96% 0.018 78);
  border: 1px solid var(--rule-strong);
  border-radius: 2px;
  color: var(--ink);
  letter-spacing: 0.4em;
}
input:focus { border-color: var(--indigo); outline: none; }
button {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  padding: 18px;
  background: var(--indigo);
  color: var(--parchment);
  border-radius: 2px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
}
button:hover { filter: brightness(1.08); }
.err {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--crimson);
  letter-spacing: 0.06em;
  min-height: 14px;
  font-weight: 700;
  margin-top: 6px;
}
.hint {
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
  text-transform: uppercase;
  text-align: center;
  margin-top: 10px;
}
</style>
<section class="cover">
  <div class="top">
    <span>Vol. I</span>
    <span class="center">वसीयत<span class="dot"></span>The Vault<span class="dot"></span>Issue I</span>
    <span id="date"></span>
  </div>
  <div class="mark">वसीयत</div>
  <div class="en">Wasiyat</div>
  <div class="quote">A door without a key keeps no promise.</div>
  <div class="attr">— वसीयत proverb</div>
  <form id="form">
    <input id="pass" type="password" placeholder="passcode" autocomplete="current-password" autocapitalize="off" autocorrect="off" />
    <button type="submit">Open the vault</button>
    <div class="err" id="err" role="alert"></div>
  </form>
  <div class="hint">on-device · encrypted · nothing leaves this page</div>
</section>
`;

export class WasiyatGate extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const date = this.#shadow.getElementById('date');
    if (date) date.textContent = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    const pass = this.#shadow.getElementById('pass') as HTMLInputElement;
    const err = this.#shadow.getElementById('err') as HTMLDivElement;
    queueMicrotask(() => pass.focus());

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      const code = pass.value;
      if (!code) return;
      try {
        const env = await loadEnvelope();
        if (!env) {
          err.textContent = 'no vault on this device';
          return;
        }
        const vault = await decryptVault<Vault>(code, env);
        const apply = (): void => store.setVault(vault, code);
        if ('startViewTransition' in document) {
          (document as Document & { startViewTransition: (cb: () => void) => unknown }).startViewTransition(apply);
        } else {
          apply();
        }
      } catch {
        err.textContent = 'galat passcode · try again';
        pass.value = '';
        pass.focus();
      }
    });
  }
}

customElements.define('wasiyat-gate', WasiyatGate);
